/**
 * ชนิดข้อมูลของ Tool/Die Master (D-108 ข้อ 4 และ 5) — เขียนตามที่
 * `apps/api/src/cap_erp_api/modules/master_data/router.py`/`schemas.py`/`tooling.py`
 * serve จริง (`WO-P2-M11-009`) ไม่ใช่ตาม `docs/API_CONTRACT.md`
 *
 * สี่ข้อที่ต้องอ่านออกจากไฟล์นี้โดยไม่ต้องเปิดเอกสาร:
 *
 * 1. **`cumulative_life_count` และ `life_since_last_maintenance` เป็นสตริงเสมอ**
 *    (`AmountOut` ฝั่งเซิร์ฟเวอร์ — `NUMERIC(18,4)` ที่ serialize เป็น string เพื่อไม่
 *    ให้ IEEE-754 double ปัดเลข) และ **ไม่มีทั้งคู่ในคำขอใดเลย** เพราะไม่มีคอลัมน์ใด
 *    เก็บมันจริง (D-108 ข้อ 5) — ดูหมายเหตุยาวที่ `SERVER_OWNED_TOOL_FIELDS`
 * 2. **สองตัวนับตอบคำถามคนละข้อ และห้ามรวมเป็นตัวเดียว** — `cumulative_life_count`
 *    คือทั้งชีวิตของเครื่องมือ (ใช้ตัดสินใจซื้อใหม่) ส่วน `life_since_last_maintenance`
 *    ตัดที่ PM ล่าสุด (ใช้ตัดสินใจนัดซ่อมบำรุงครั้งถัดไป) — `references/tooling-mold-management.md`
 *    เขียนตรง ๆ ว่า "ห้ามรวมเป็นตัวเดียว"
 * 3. **`rated_life_shots` เป็นสเปก ไม่ใช่ขีดจำกัดที่ระบบบังคับ** — เครื่องมือที่เกิน
 *    เรตติ้งยังใช้งานได้ปกติ เป็นแค่สัญญาณให้วางแผนเปลี่ยน
 * 4. **สองความสัมพันธ์เป็น many-to-many ทั้งคู่** — เครื่องมือหนึ่งตัวผลิตได้หลาย
 *    part number และใช้ได้กับหลายเครื่องจักร ประกอบกันชั่วคราว ไม่ใช่จับคู่ถาวร
 */

/**
 * รหัสโมดูลของหน้าจอนี้ใน RBAC matrix — **`PRODUCT_MASTER` เดิม ไม่ใช่โมดูลใหม่**
 * (`router.py`: "No new module code" — เหตุผลเดียวกับที่ D-105/D-086 ให้กับ packing
 * standard และกฎตรวจรับเข้า: ดูแลเครื่องมือเป็นงานของคนกลุ่มเดียวกับที่ดูแล BOM/Routing)
 *
 * ประกาศซ้ำแทนการ import ข้ามโดเมนด้วยเหตุผลเดียวกับทุกโดเมนอื่นในโปรเจกต์นี้
 */
export const PRODUCT_MASTER_MODULE = 'PRODUCT_MASTER';

/* ------------------------------------------------------- ข้อจำกัดความยาว -- */

/** `tool_registry.tool_code` — `bom_routing.py TOOL_CODE_LENGTH` */
export const TOOL_CODE_MAX_LENGTH = 40;
/** `tool_registry.spec` — `tooling.py SPEC_LENGTH` */
export const SPEC_MAX_LENGTH = 255;
/** `tool_maintenance_event.note` — `tooling.py MAINTENANCE_NOTE_LENGTH` */
export const MAINTENANCE_NOTE_MAX_LENGTH = 255;

/* ------------------------------------------------------------- enum ค่า --- */

/** ห้าชนิดที่ `tooling.py ToolType` ประกาศ — ไม่มีตัวที่หก */
export const TOOL_TYPES = ['DIE', 'MOLD', 'JIG', 'FIXTURE', 'GAUGE'] as const;
export type ToolTypeValue = (typeof TOOL_TYPES)[number];

export function isToolType(value: string): value is ToolTypeValue {
  return (TOOL_TYPES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------- resource --- */

/**
 * เครื่องมือหนึ่งตัว ตาม `ToolResponse` — **สองตัวนับเป็นสตริงเสมอ** (ดูหมายเหตุ
 * ข้อ 1 ของไฟล์) และคำนวณสดทุกครั้งที่อ่าน ไม่ใช่ค่าที่เก็บไว้
 */
export interface Tool {
  readonly tool_id: string;
  readonly tool_code: string | null;
  readonly tool_type: string | null;
  readonly spec: string | null;
  readonly rated_life_shots: number | null;
  readonly cumulative_life_count: string;
  readonly life_since_last_maintenance: string;
  readonly last_maintenance_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/** `GET /v1/tools` — envelope มาตรฐาน (ไม่มี pagination — ทะเบียนเครื่องมือของโรงงาน
 * เดียวไม่ถึงระดับที่ต้องแบ่งหน้า) */
export interface ToolListPage {
  readonly items: readonly Tool[];
}

/** `(tool_id, product_id)` — ส่วนประกอบที่เครื่องมือตัวนี้ผลิตได้ */
export interface ToolProduct {
  readonly tool_id: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly created_at: string;
}

export interface ToolProductListPage {
  readonly tool_id: string;
  readonly items: readonly ToolProduct[];
}

/** `(tool_id, work_center_id)` — เครื่องจักรที่เครื่องมือตัวนี้ใช้ได้ */
export interface ToolWorkCenter {
  readonly tool_id: string;
  readonly work_center_id: string;
  readonly work_center_code: string;
  readonly plant_code: string;
  readonly created_at: string;
}

export interface ToolWorkCenterListPage {
  readonly tool_id: string;
  readonly items: readonly ToolWorkCenter[];
}

/** หนึ่งการซ่อมบำรุง — เขียนครั้งเดียว ไม่มีการแก้ไข */
export interface ToolMaintenanceEvent {
  readonly event_id: string;
  readonly tool_id: string;
  readonly performed_at: string;
  readonly performed_by_employee_id: string;
  readonly note: string | null;
  readonly created_at: string;
}

export interface ToolMaintenanceEventListPage {
  readonly tool_id: string;
  readonly items: readonly ToolMaintenanceEvent[];
}

/* -------------------------------------------------------------- requests -- */

/**
 * body ของ `POST /v1/tools` — ทุกฟิลด์ไม่บังคับ (มักลงทะเบียนก่อนมีข้อมูลครบ)
 * **ไม่มีสองตัวนับ** เพราะเซิร์ฟเวอร์เป็นเจ้าของทั้งคู่
 */
export interface CreateToolBody {
  readonly tool_code?: string;
  readonly tool_type?: ToolTypeValue;
  readonly spec?: string;
  readonly rated_life_shots?: number;
}

/**
 * body ของ `PATCH /v1/tools/{tool_id}` — ทั้งสี่คอลัมน์ nullable ฝั่งเซิร์ฟเวอร์
 * `null` ตรง ๆ แปลว่า "ลบค่านี้" ส่วนคีย์ที่ไม่ส่งแปลว่า "ปล่อยไว้เหมือนเดิม"
 * ฟอร์มแก้ไขในหน้าจอนี้ส่งครบทั้งสี่คีย์เสมอ (ฟอร์มเต็มที่แสดงค่าปัจจุบัน) — ช่องที่
 * เว้นว่างไว้จึงเป็นการ "ลบ" โดยตั้งใจ ไม่ใช่การ "ไม่แตะ"
 */
export interface PatchToolBody {
  readonly tool_code: string | null;
  readonly tool_type: ToolTypeValue | null;
  readonly spec: string | null;
  readonly rated_life_shots: number | null;
}

/** body ของ `POST /v1/tools/{tool_id}/products` */
export interface LinkToolProductBody {
  readonly product_id: string;
}

/** body ของ `POST /v1/tools/{tool_id}/work-centers` */
export interface LinkToolWorkCenterBody {
  readonly work_center_id: string;
}

/**
 * body ของ `POST /v1/tools/{tool_id}/maintenance-events` — `performed_at` บังคับ
 * และเป็น **เมื่อไรที่ซ่อมจริง** ไม่ใช่เมื่อไรที่พิมพ์แถวนี้ (ต่างจาก `created_at`)
 */
export interface RecordToolMaintenanceBody {
  readonly performed_at: string;
  readonly note?: string;
}

/**
 * ฟิลด์ของเครื่องมือที่ไม่มีคำขอใดส่งได้ — คัดลอกจาก
 * `schemas.py SERVER_OWNED_TOOL_FIELDS` ตรง ๆ เพื่ออ่านคำตอบ `400
 * TOOL_FIELD_IS_SERVER_OWNED` ให้ตรงกับกฎจริง — **สองตัวนับคือเหตุผลทั้งหมดที่กฎนี้
 * มีอยู่** (D-108 ข้อ 5): ไม่มีคอลัมน์ใดเก็บมัน ดังนั้นจึงไม่มีทางส่งไปแก้ได้เลย
 */
export const SERVER_OWNED_TOOL_FIELDS = [
  'tool_id',
  'cumulative_life_count',
  'life_since_last_maintenance',
  'last_maintenance_at',
] as const;
